import React, { useState } from 'react';
import { Button, Col, Descriptions, Form, Input, InputNumber, Modal, Row, message } from 'antd';
import api from '../../../authentication/axiosInstance';

export const PLOW_FIELDS = [
  { name: 'plow_name', label: 'Name', required: true },
  { name: 'plow_type', label: 'Type' },
  { name: 'plow_size', label: 'Size' },
  { name: 'plow_brand', label: 'Brand' },
  { name: 'plow_model', label: 'Model' },
  { name: 'plow_serial_number', label: 'Serial Number' },
  { name: 'plow_group', label: 'Group' },
  { name: 'multipliers', label: 'Multiplier', number: true, editOnly: true },
];

export const DOUBLE_DOWN_FIELDS = [
  { name: 'doubleDown_name', label: 'Name', required: true },
  { name: 'doubleDown_type_name', label: 'Type' },
  { name: 'doubleDown_brand', label: 'Brand' },
  { name: 'doubleDown_model', label: 'Model' },
];

const CONFIG = {
  plow: { kind: 'Plow', fields: PLOW_FIELDS, idField: 'plow_id', endpoint: '/plows/' },
  doubledown: { kind: 'DoubleDown', fields: DOUBLE_DOWN_FIELDS, idField: 'doubleDown_id', endpoint: '/double-down/' },
};

export const getFormFields = (itemType, mode) => {
  const { fields } = CONFIG[itemType];
  if (mode === 'edit') return fields;
  // The API rejects blank text on create, so every text field is required there.
  return fields.filter((field) => !field.editOnly).map((field) => ({ ...field, required: true }));
};

const normalizeValue = (field, value) => {
  if (field.number) return value === undefined || value === '' ? null : value;
  return typeof value === 'string' ? value.trim() : (value ?? '');
};

export const buildCreatePayload = (fields, values) => Object.fromEntries(
  fields.map((field) => [field.name, normalizeValue(field, values[field.name])]),
);

// PATCH only what changed: Fleetio-imported rows can hold blank values the API rejects on write.
export const buildDdPlowChanges = (fields, original, values) => fields.reduce((changes, field) => {
  const next = normalizeValue(field, values[field.name]);
  if (next !== normalizeValue(field, original[field.name])) {
    changes[field.name] = next;
  }
  return changes;
}, {});

export const formatApiError = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  return Object.values(data).flat().filter((value) => typeof value === 'string').join(' ');
};

const yesNo = (value) => (value ? 'Yes' : 'No');

// Mounted fresh for every open so no typed values carry over between records or between add and edit.
const DdPlowForm = ({ itemType, mode, record, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = mode === 'edit';
  const config = CONFIG[itemType];
  const fields = getFormFields(itemType, mode);
  const original = (isEdit && record?.raw) || {};
  const verb = isEdit ? 'update' : 'create';

  const handleFinish = async (values) => {
    setSaving(true);
    try {
      if (isEdit) {
        const changes = buildDdPlowChanges(fields, original, values);
        if (Object.keys(changes).length === 0) {
          onClose();
          return;
        }
        await api.patch(`${config.endpoint}${original[config.idField]}/`, changes);
      } else {
        await api.post(config.endpoint, buildCreatePayload(fields, values));
      }
      message.success(`${config.kind} ${verb}d successfully`);
      onSaved();
    } catch (error) {
      console.error(`Failed to ${verb} ${config.kind}:`, error);
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        form.setFields(fields
          .filter((field) => data[field.name])
          .map((field) => ({ name: field.name, errors: [].concat(data[field.name]) })));
      }
      const detail = formatApiError(data);
      message.error(`Failed to ${verb} ${config.kind}${detail ? `: ${detail}` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      form={form}
      initialValues={original}
      onFinish={handleFinish}
      layout="vertical"
    >
      <Row gutter={16}>
        {fields.map((field) => (
          <Col span={12} key={field.name}>
            <Form.Item
              name={field.name}
              label={field.label}
              rules={field.required ? [{ required: true, whitespace: true, message: `Please input the ${config.kind.toLowerCase()} ${field.label.toLowerCase()}!` }] : []}
            >
              {field.number ? <InputNumber style={{ width: '100%' }} /> : <Input />}
            </Form.Item>
          </Col>
        ))}
      </Row>
      {isEdit && (
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Assigned Vehicle">{original.assigned_vehicle?.vehicle_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Assigned">{yesNo(original.assigned_bool)}</Descriptions.Item>
          {itemType === 'plow' && (
            <Descriptions.Item label="Confirmed">{yesNo(original.is_confirmed)}</Descriptions.Item>
          )}
        </Descriptions>
      )}
      <Form.Item style={{ marginTop: '24px' }}>
        <Button type="primary" htmlType="submit" size="large" block loading={saving}>
          {isEdit ? 'Update' : 'Create'} {config.kind}
        </Button>
      </Form.Item>
    </Form>
  );
};

const DdPlowFormModal = ({ itemType, mode = 'edit', record, onClose, onSaved }) => {
  const config = itemType ? CONFIG[itemType] : null;

  return (
    <Modal
      title={config && `${mode === 'edit' ? 'Edit' : 'Add'} ${config.kind}`}
      open={Boolean(config)}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {config && (
        <DdPlowForm
          key={`${mode}-${itemType}-${record?.key || 'new'}`}
          itemType={itemType}
          mode={mode}
          record={record}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Modal>
  );
};

export default DdPlowFormModal;

